import { useState, useEffect, useCallback } from 'react';
import { holidayAPI } from '../services/index';
import { Holiday } from '../types/api';

/**
 * 휴일 데이터를 관리하는 커스텀 훅
 * 여러 컴포넌트에서 반복되는 휴일 데이터 로드 로직을 통합
 */
export const useHolidays = (initialDate?: Date) => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(initialDate || new Date());

  // 휴일 데이터 로드
  const fetchHolidays = useCallback(async (targetDate: Date) => {
    setLoading(true);
    setError(null);
    try {
      const response = await holidayAPI.getHolidays({
        year: targetDate.getFullYear(),
        month: targetDate.getMonth() + 1
      });
      if (response.data?.success && response.data?.data) {
        setHolidays(response.data.data);
      }
    } catch {
      setError('휴일 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 월이 변경될 때 휴일 데이터 로드
  useEffect(() => {
    fetchHolidays(currentMonth);
  }, [currentMonth, fetchHolidays]);

  // 특정 날짜가 휴일인지 확인
  const isHoliday = useCallback((date: Date | string): boolean => {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    return holidays.some(h => {
      // startDate와 endDate 범위 내에 있는지 확인
      const start = h.startDate;
      const end = h.endDate || h.startDate;
      return dateStr >= start && dateStr <= end;
    });
  }, [holidays]);

  // 특정 날짜의 휴일 정보 가져오기
  const getHolidayInfo = useCallback((date: Date | string): Holiday | undefined => {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    return holidays.find(h => {
      const start = h.startDate;
      const end = h.endDate || h.startDate;
      return dateStr >= start && dateStr <= end;
    });
  }, [holidays]);

  // 월 변경 시 호출
  const handleMonthChange = useCallback((date: Date) => {
    setCurrentMonth(date);
  }, []);

  // 수동으로 휴일 데이터 새로고침
  const refresh = useCallback(() => {
    fetchHolidays(currentMonth);
  }, [currentMonth, fetchHolidays]);

  return {
    holidays,
    loading,
    error,
    currentMonth,
    isHoliday,
    getHolidayInfo,
    handleMonthChange,
    refresh
  };
};

export default useHolidays;
